
import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { constants } from '../constants/constants';
import { AuthService } from '../services/auth.service';
import { SILENT_NOT_FOUND } from './http-context';

export const AuthInterceptor: HttpInterceptorFn = (request, next) => {
    const toast = inject(ToastService);
    const auth = inject(AuthService);

    return next(request).pipe(
        catchError((error: HttpErrorResponse) => {
            handleHttpError(error, toast, auth, request);
            return throwError(() => error);
        }),
    );
};

function handleHttpError(error: HttpErrorResponse, toast: ToastService, auth: AuthService, request: any): void {
    if (isUnauthorizedOrForbidden(error.status)) {
        handleUnauthorizedError(error, toast, auth);
        return;
    }

    if (error.status === HttpStatusCode.NotFound && request.context?.get(SILENT_NOT_FOUND)) {
        // The caller opted in (see http-context.ts) and renders the not-found state itself.
        return;
    }

    if (error.status === constants.unknown_error.status_code) {
        toast.showError(constants.unknown_error.message);
        return;
    }

    if (error.status === HttpStatusCode.InternalServerError) {
        handleInternalServerError(error, toast);
        return;
    }

    handleGeneralError(error, toast, request);
}

function isUnauthorizedOrForbidden(status: number): boolean {
    return [HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(status);
}

function handleUnauthorizedError(error: HttpErrorResponse, toast: ToastService, auth: AuthService): void {
    if (auth.isAuthenticated()) {
        toast.showError("Session Timed Out!");
    } else if (hasValidErrorMessage(error)) {
        toast.showError(error.error.message);
    } else {
        toast.showError(constants.internalServerError.defaultErrorMessage);
    }
    auth.logout(true);
}

function hasValidErrorMessage(error: HttpErrorResponse): boolean {
    return error && error.message && error.error.message && typeof error.error.message === 'string';
}

function handleInternalServerError(error: HttpErrorResponse, toast: ToastService): void {
    const err = error.error || null;
    if (!err) {
        toast.showError(constants.internalServerError.defaultErrorMessage);
        return;
    }

    try {
        const errorMessage = extractErrorMessage(err);
        toast.showError(errorMessage);
    } catch (parseError) {
        toast.showError(constants.internalServerError.defaultErrorMessage);
    }
}

function extractErrorMessage(err: any): string {
    if (err.message && typeof err.message === 'string') {
        return err.message;
    }

    if (err.message && Array.isArray(err.message) && err.message.length) {
        return err.message[0];
    }

    const parsedErr = JSON.parse(err);
    return parsedErr.message;
}

function handleGeneralError(error: HttpErrorResponse, toast: ToastService, request: any): void {
    // Suppress error 1002 for late accept offer(handled via modal)
    if (!request.url.includes(constants.url.pastProject) && error.error._code !== 1002) {
        try {
            if(typeof error.error === 'string'){
                const parsedError = JSON.parse(error.error);
                toast.showError(parsedError.message);
            } else {
                toast.showError(error.error.message);
            }
        } catch (error) {
            toast.showError('Something went wrong');
        }
    }
} 
